import {ComponentStore} from '@ngrx/component-store';
import {ReportPathExplorerState} from './report-path-explorer';
import {Injectable} from '@angular/core';
import {Observable, of, Subscription, switchMap, withLatestFrom} from 'rxjs';
import {ReportStore} from './report.store';
import {AdReportParams, AdRequestParamsPathExplorer} from '../../interfaces/ad-reports';
import {catchError, filter, tap} from 'rxjs/operators';
import {CellConversionPathService} from '../../services/cell-conversion-path.service';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {ReportPathExplorerService} from '../../services/report-path-explorer.service';
import {Serializer} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {ReportStoreState} from './report';
import {FilterConversionPathService} from '../../services/filter-conversion-path.service';

@Injectable()
export class ReportPathExplorerStore extends ComponentStore<ReportPathExplorerState> {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly loading$: Observable<boolean> = this.select((state: ReportPathExplorerState): boolean => state.loading);
  public readonly report$: Observable<any> = this.select((state: ReportPathExplorerState): any => state.report);
  public readonly data$: Observable<Array<any>> = this.select((state: ReportPathExplorerState): Array<any> => state.data);
  public readonly totals$: Observable<Array<any>> = this.select((state: ReportPathExplorerState): Array<any> => state.totals);

  private readonly _load: () => void = this.effect((params$: Observable<void>): Observable<any> => params$.pipe(
    withLatestFrom(this._reportStore.state$, this._dataset$, this._cellConversionPathService.viewChanges$),
    filter(([_, reportStoreState]: [void, ReportStoreState, Serializer<Site>, 'channel' | 'campaign']): boolean => reportStoreState.submitted),
    tap(([_, reportStoreState]: [void, ReportStoreState, Serializer<Site>, 'channel' | 'campaign']): void => {
      if ((reportStoreState.requestParams as any).view) {
        this._cellConversionPathService.viewChanges$.next((reportStoreState.requestParams as any).view);
      }

      if ((reportStoreState.requestParams as any).filter_channels) {
        this._filterConversionPathService.setFilter(
          (reportStoreState.requestParams as any).filter_channels,
          (reportStoreState.requestParams as any).filter_touchpoints
        );
      }

      this.patchState({ loading: true });
    }),
    switchMap(([_, reportStoreState, dataset, view]: [void, ReportStoreState, Serializer<Site>, 'channel' | 'campaign']): Observable<any> => this._pathExplorerService.generate(dataset, reportStoreState.requestParams as AdRequestParamsPathExplorer).pipe(
      tap((report: any): void => {
        this.patchState({
          report: report,
          data: report[(reportStoreState.requestParams as any).view || view],
          totals: report.totals,
          loading: false
        });
      }),
      catchError(() => {
        this.patchState({ loading: false });

        return of();
      })
    ))
  ));

  private readonly _reportParams$: Subscription = this.effect((): Observable<Partial<AdReportParams>> => this._reportStore.reportParamsSubmitted$.pipe(
    tap((params: Partial<AdReportParams>): void => {
      this._load();
    })
  ));

  private readonly _view$: Subscription = this.effect(() => this._cellConversionPathService.viewChanges$.pipe(
    withLatestFrom(this.state$),
    filter(([_, state]: ['campaign' | 'channel', ReportPathExplorerState]): boolean => state.report !== null),
    tap(([view, state]: ['campaign' | 'channel', ReportPathExplorerState]): void => {
      this.patchState({
        data: state.report[view]
      });

      this._reportStore.updateRequestParamsSubmitted({ view: view } as any);
    })
  ));

  private readonly _tableFilters$: Subscription = this.effect((): Observable<any> => this._filterConversionPathService.set.pipe(
    tap((filters: { numberOfChannels: 'all' | number, numberOfTouchpoints: 'all' | number }): void => {
      this._reportStore.updateRequestParamsSubmitted({
        filter_channels: filters.numberOfChannels,
        filter_touchpoints: filters.numberOfTouchpoints
      } as any);
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _pathExplorerService: ReportPathExplorerService,
    private readonly _cellConversionPathService: CellConversionPathService,
    private readonly _filterConversionPathService: FilterConversionPathService
  ) {
    super({
      report: null,
      data: [],
      totals: [{conversion_path: null, conversions: {value: '-', formatted: '-'}, revenue: {value: '-', formatted: '-'}}],
      loading: false
    });
  }

}
